/**
 * Sends a browser crash to the server so it lands in the same log as everything else.
 *
 * Deliberately not the shared `api()` helper. That one redirects to /login on a 401 and throws on
 * failure — both wrong here. A crash report is best-effort by definition: if it cannot be
 * delivered, the answer is to give up quietly, never to navigate the user somewhere or to throw a
 * second error out of the handler for the first one.
 */

const ENDPOINT = '/api/client-errors';

/** Vite substitutes this at build time; it tells one deploy's stack traces from another's. */
const RELEASE = import.meta.env.MODE;

/**
 * Same message twice is the same bug. A component that throws on every render would otherwise
 * send a report per attempt, which costs the user their rate-limit budget and tells us nothing the
 * first one did not.
 */
const seen = new Set<string>();

/** A hard ceiling as well, in case the messages themselves vary (a loop over an index, say). */
const MAX_PER_SESSION = 10;
let sent = 0;

export type ClientErrorKind = 'boundary' | 'uncaught' | 'recoverable';

export function reportClientError(
  error: unknown,
  kind: ClientErrorKind,
  componentStack?: string | null,
): void {
  const err = error instanceof Error ? error : new Error(String(error));
  const key = `${kind}:${err.message}`;

  if (sent >= MAX_PER_SESSION || seen.has(key)) {
    return;
  }

  seen.add(key);
  sent += 1;

  const body = JSON.stringify({
    message: err.message,
    stack: err.stack ?? null,
    componentStack: componentStack ?? null,
    route: window.location.pathname,
    kind,
    release: RELEASE,
  });

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };

  // The endpoint is CSRF-exempt, because the landing page is served straight from wwwroot and
  // never issues a token — so a crash during the first render would otherwise be the one report
  // that could not be sent. The token is still echoed when there is one: it costs nothing, and it
  // means a report from inside the running app is indistinguishable from any other request it
  // makes. Read from the cookie directly rather than through the api client, so this module stays
  // free of anything that could itself be what broke.
  const token = document.cookie
    .split('; ')
    .find((row) => row.startsWith('XSRF-TOKEN='))
    ?.split('=')[1];

  if (token) {
    headers['X-XSRF-TOKEN'] = decodeURIComponent(token);
  }

  void fetch(ENDPOINT, {
    method: 'POST',
    headers,
    body,
    credentials: 'include',
    // The page may be on its way to a reload from the fallback's own button.
    keepalive: true,
  }).catch(() => {
    /* Nowhere left to report a failure to report. */
  });
}
